import { apiConfig, EnumAPIKey } from "@/constants/app/api"
import { EnumEndpoints } from "@/constants/app/type"
import {
  EMPTY_SYSTEM_TIERED_DISCOUNT,
  ParsedSystemTieredDiscount,
  VouchersGetAllBody,
  VouchersGetAllResponseV3,
} from "@/lib/redux/features/voucher/voucherApiV3.types"
import { Voucher } from "@/lib/redux/features/voucher/type"
import { normalizeVouchersGetAllResponse } from "./normalizeVoucherCategories"
import { parseSystemTieredDiscounts } from "./parseSystemTieredDiscounts"
import { toVouchersGetAllApiPayload } from "./buildVouchersGetAllBody"
import {
  getPublicVoucherListSnapshot,
  savePublicVoucherListSnapshot,
} from "@/lib/pwa/voucherListSnapshotStore"

const getAccessToken = () =>
  typeof localStorage !== "undefined" ? localStorage.getItem("apiAccessToken") : null

const inflightByKey = new Map<string, Promise<Voucher[]>>()
const cacheByKey = new Map<string, Voucher[]>()
/**
 * DOP-942: cấu hình SYSTEM_TIERED_DISCOUNT chỉ có ở response THÔ (categories → conditions) — bị mất
 * sau normalize. Vì `callerService` trả về `Voucher[]` nên callback không đọc được categories; ta
 * parse ngay tại đây (nơi còn `json` thô) và stash theo cùng cache-key để `callbackVoucherGetAll`
 * đọc lại đồng bộ qua `action.meta.arg.body`.
 */
const tieredByKey = new Map<string, ParsedSystemTieredDiscount>()

const getCacheKey = (body: VouchersGetAllBody) =>
  JSON.stringify(toVouchersGetAllApiPayload(body))

/** Cấu hình trừ dần đã parse cho body tương ứng (rỗng nếu chưa fetch thô / offline). */
export const getCachedSystemTieredDiscount = (
  body?: VouchersGetAllBody | null,
): ParsedSystemTieredDiscount => {
  if (!body) return EMPTY_SYSTEM_TIERED_DISCOUNT
  return tieredByKey.get(getCacheKey(body)) ?? EMPTY_SYSTEM_TIERED_DISCOUNT
}

async function fetchVouchersAllOnce(body: VouchersGetAllBody): Promise<Voucher[]> {
  const cacheKey = getCacheKey(body)
  const endpoint = `${apiConfig[EnumAPIKey.API_BASE]}${EnumEndpoints.VOUCHER_GET_ALL}`
  const token = getAccessToken()

  try {
    const res = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(toVouchersGetAllApiPayload(body)),
    })

    if (!res.ok) {
      return []
    }

    const json = await res.json().catch(() => null)
    tieredByKey.set(
      cacheKey,
      parseSystemTieredDiscounts((json as VouchersGetAllResponseV3 | null)?.categories),
    )
    const vouchers = normalizeVouchersGetAllResponse(json)
    await savePublicVoucherListSnapshot(cacheKey, vouchers)
    return vouchers
  } catch (error) {
    const snapshot = await getPublicVoucherListSnapshot(cacheKey)
    if (snapshot) {
      // Snapshot offline chỉ lưu Voucher[] (không có cấu hình trừ dần) → giữ giá gốc, không áp mã.
      tieredByKey.set(cacheKey, EMPTY_SYSTEM_TIERED_DISCOUNT)
      return snapshot.data
    }

    throw error
  }
}

/** Dedupe theo payload — mọi FlightItem dùng chung 1 request in-flight / cache. */
export async function fetchVouchersAll(body: VouchersGetAllBody): Promise<Voucher[]> {
  const key = getCacheKey(body)

  if (cacheByKey.has(key)) {
    return cacheByKey.get(key)!
  }

  const inflight = inflightByKey.get(key)
  if (inflight) {
    return inflight
  }

  const request = fetchVouchersAllOnce(body)
    .then((vouchers) => {
      cacheByKey.set(key, vouchers)
      inflightByKey.delete(key)
      return vouchers
    })
    .catch((err) => {
      inflightByKey.delete(key)
      throw err
    })

  inflightByKey.set(key, request)
  return request
}

export const seedFetchVouchersAllCache = (
  body: VouchersGetAllBody,
  vouchers: Voucher[],
) => {
  cacheByKey.set(getCacheKey(body), vouchers)
}

export const clearFetchVouchersAllCache = () => {
  cacheByKey.clear()
  inflightByKey.clear()
  tieredByKey.clear()
}
